import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Share2, Users, User, X, Check } from 'lucide-react';
import { Modal } from './Modal';
import { api } from '../lib/api';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  personalVariantId: number;
  bandId: number;
}

export function ShareModal({ open, onClose, personalVariantId, bandId }: ShareModalProps) {
  const [mode, setMode] = useState<'band' | 'members'>('band');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: members = [] } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
    enabled: open,
  });

  const { data: shares = [] } = useQuery({
    queryKey: ['variantShares', personalVariantId],
    queryFn: () => api.charts.getPersonalShares(personalVariantId),
    enabled: open,
  });

  const shareMutation = useMutation({
    mutationFn: (data: { bandId?: number; userIds?: string[] }) => api.charts.sharePersonal(personalVariantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variantShares', personalVariantId] });
      setSelectedUserIds([]);
    },
  });

  const removeShareMutation = useMutation({
    mutationFn: (shareId: number) => api.charts.removePersonalShare(personalVariantId, shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variantShares', personalVariantId] });
    },
  });

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleShare = () => {
    if (mode === 'band') {
      shareMutation.mutate({ bandId });
    } else {
      if (selectedUserIds.length === 0) return;
      shareMutation.mutate({ userIds: selectedUserIds });
    }
  };

  const bandShare = shares.find((s: any) => s.sharedToBandId === bandId);
  const userShares = shares.filter((s: any) => s.sharedToUserId);

  return (
    <Modal open={open} onClose={onClose} title="Share My Version">
      <div className="space-y-5">
        <p className="text-sm text-gray-400">Share your personal chart version with band members so they can view your notes and arrangements.</p>

        {shares.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Currently Shared With</h4>
            <div className="space-y-1.5">
              {bandShare && (
                <div className="flex items-center justify-between p-2.5 bg-surface-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-400" />
                    <span className="text-sm">Entire Band</span>
                  </div>
                  <button
                    onClick={() => removeShareMutation.mutate(bandShare.id)}
                    className="p-1 hover:bg-red-900/30 rounded transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              )}
              {userShares.map((share: any) => (
                <div key={share.id} className="flex items-center justify-between p-2.5 bg-surface-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-accent-400" />
                    <span className="text-sm">{share.sharedToUserName || share.sharedToUserId}</span>
                  </div>
                  <button
                    onClick={() => removeShareMutation.mutate(share.id)}
                    className="p-1 hover:bg-red-900/30 rounded transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Share With</h4>
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setMode('band')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                mode === 'band' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Entire Band
            </button>
            <button
              onClick={() => setMode('members')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                mode === 'members' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              Select Members
            </button>
          </div>

          {mode === 'members' && (
            <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
              {members.map((m: any) => (
                <button
                  key={m.userId}
                  onClick={() => toggleUser(m.userId)}
                  className={`flex items-center gap-3 w-full p-2.5 rounded-lg text-left transition-colors ${
                    selectedUserIds.includes(m.userId) ? 'bg-primary-600/20 border border-primary-500/30' : 'bg-surface-800 hover:bg-surface-700'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-surface-600 flex items-center justify-center text-xs font-medium shrink-0">
                    {m.profileImageUrl ? (
                      <img src={m.profileImageUrl} className="w-7 h-7 rounded-full object-cover" alt="" />
                    ) : (
                      (m.firstName?.[0] || m.email?.[0] || '?').toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm block truncate">{m.firstName ? `${m.firstName} ${m.lastName || ''}`.trim() : m.email}</span>
                    <span className="text-xs text-gray-500 block truncate">{m.email}</span>
                  </div>
                  {selectedUserIds.includes(m.userId) && (
                    <Check className="w-4 h-4 text-primary-400 shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={handleShare}
            disabled={shareMutation.isPending || (mode === 'members' && selectedUserIds.length === 0)}
            className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm"
          >
            <Share2 className="w-4 h-4" />
            {shareMutation.isPending ? 'Sharing...' : mode === 'band' ? 'Share with Band' : `Share with ${selectedUserIds.length} Member${selectedUserIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
